"use client";

import * as React from "react";
import { Search, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Input } from "../input";
import { DataTableViewOptions } from "./data-table-view-options";
import type { DataTableToolbarProps } from "./types";

export function DataTableToolbar<TData>({
  table,
  searchPlaceholder = "Search...",
  searchDebounceMs = 300,
  enableSearch = true,
  enableColumnVisibility = true,
  children,
  className,
}: DataTableToolbarProps<TData>) {
  const globalFilter = table.getState().globalFilter as string;
  const [searchValue, setSearchValue] = React.useState(globalFilter ?? "");

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      table.setGlobalFilter(searchValue);
    }, searchDebounceMs);
    return () => clearTimeout(timeout);
  }, [searchValue, searchDebounceMs, table]);

  React.useEffect(() => {
    const normalized = globalFilter ?? "";
    if (normalized !== searchValue) {
      setSearchValue(normalized);
    }
    // Only sync when external globalFilter changes, not when searchValue changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter]);

  return (
    <div
      role="toolbar"
      aria-label="Table filters"
      className={cn(
        "flex h-toolbar shrink-0 items-center gap-1 px-4",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {enableSearch && (
          <div className="relative shrink-0">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-tertiary"
            />
            <Input
              inputSize="sm"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-37.5 pl-7 pr-7 md:w-62.5"
            />
            {searchValue && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchValue("")}
                className={cn(
                  "absolute right-1 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-sm",
                  "text-text-tertiary",
                  "transition-[background-color,color] duration-100 ease-out-fast",
                  "hover:bg-hover hover:text-text",
                  "focus-visible:outline-none focus-fill",
                )}
              >
                <X aria-hidden="true" className="size-3.5" />
              </button>
            )}
          </div>
        )}
        {children}
      </div>
      {enableColumnVisibility && <DataTableViewOptions table={table} />}
    </div>
  );
}

DataTableToolbar.displayName = "DataTableToolbar";
